<template>
	<auth-layout
		headline="欢迎回来"
		subtext="登录您的账号，继续处理消息与协作。"
		mobile-desc="账号登录"
	>
		<template #preview>
			<div class="preview-card">
				<div class="preview-msg preview-msg--left">
					<div class="preview-avatar"></div>
					<div class="preview-bubble">你好，有空聊聊吗？</div>
				</div>
				<div class="preview-msg preview-msg--right">
					<div class="preview-bubble preview-bubble--primary">当然，随时在线 😊</div>
					<div class="preview-avatar preview-avatar--primary"></div>
				</div>
				<div class="preview-msg preview-msg--left">
					<div class="preview-avatar"></div>
					<div class="preview-bubble">消息已加密，安全传输中...</div>
				</div>
				<div class="preview-status">
					<i class="el-icon-lock"></i> 端到端加密保护
				</div>
			</div>
		</template>

		<!-- 登录卡片 -->
		<div class="auth-card">
			<div class="card-header">
				<div class="card-title-row">
					<div class="card-title">登录</div>
					<button
						type="button"
						class="tenant-context-switcher"
						:class="{ 'tenant-context-switcher--empty': !tenantKey }"
						@click="onSwitchTenant"
					>
						<i class="el-icon-office-building"></i>
						<span>{{ tenantDisplayValue }}</span>
						<strong>切换</strong>
					</button>
				</div>
				<div class="card-desc">使用账号密码或扫码继续</div>
			</div>
			<!-- 登录方式 Tab -->
			<div class="auth-tabs">
				<div class="tab-item" :class="{ active: loginMode === 'password' }" @click="loginMode = 'password'">
					<i class="el-icon-lock"></i> 密码登录
				</div>
				<div class="tab-item" :class="{ active: loginMode === 'qr' }" @click="loginMode = 'qr'">
					<i class="iconfont icon-scan"></i> 扫码登录
				</div>
			</div>
				<div class="card-content">
				<div class="login-panel-shell">
					<transition name="tab-fade" mode="out-in">
					<!-- 密码登录 -->
					<div v-if="loginMode === 'password'" key="password" class="login-panel login-panel--password">
						<el-form :model="loginForm" status-icon :rules="rules"
							ref="loginForm" @keyup.enter.native="submitForm()" v-loading="loading" element-loading-text="正在登陆...">
							<el-form-item prop="terminal" v-show="false">
								<el-input type="terminal" v-model="loginForm.terminal"></el-input>
							</el-form-item>
							<el-form-item prop="userName">
								<label class="form-label">账号</label>
								<div
									ref="credentialInputWrap"
									class="credential-input-wrap"
								>
									<el-input
										ref="userNameInput"
										v-model="loginForm.userName"
										:placeholder="loginNamePlaceholder"
										prefix-icon="el-icon-user"
										@input="onUserNameInput"
									>
										<template #suffix>
											<span
												v-if="historyLoading"
												class="credential-toggle credential-toggle--loading"
											>
												<i class="el-icon-loading"></i>
											</span>
											<button
												v-else
												type="button"
												class="credential-toggle"
												:class="{
													'credential-toggle--active': showCredentialDropdown,
													'credential-toggle--disabled': !credentialHistory.length
												}"
												:disabled="!credentialHistory.length"
												@click.stop="toggleCredentialDropdown"
											>
												<i class="el-icon-arrow-down"></i>
											</button>
										</template>
									</el-input>
									<div
										v-if="showCredentialDropdown && credentialHistory.length > 0"
										class="credential-history-dropdown"
									>
										<button
											v-for="item in credentialHistory"
											:key="`${item.tenantKey}-${item.userName}`"
											type="button"
											class="credential-history__item"
											@click="onSelectCredential(item)"
										>
											<span class="credential-history__main">
												<span class="credential-history__name">{{ item.userName }}</span>
												<span class="credential-history__meta">{{ formatCredentialSource(item.source) }} · {{ formatCredentialTime(item.updatedAt) }}</span>
											</span>
											<i
												class="el-icon-close credential-history__remove"
												@click.stop="onRemoveCredential(item)"
											></i>
										</button>
									</div>
								</div>
							</el-form-item>
							<el-form-item prop="password">
								<label class="form-label">密码</label>
								<el-input
									ref="passwordInput"
									:type="isPasswordVisible ? 'text' : 'password'"
									v-model="loginForm.password"
									placeholder="请输入密码"
									prefix-icon="el-icon-lock"
								>
									<template #suffix>
										<i
											:class="isPasswordVisible ? 'icon-pwd-show' : 'icon-pwd-hide'"
											class="iconfont password-switch-icon"
											@click="switchPasswordVisible"
										></i>
									</template>
								</el-input>
							</el-form-item>
							<div class="nav-tool-bar">
								<el-checkbox v-model="isAutoLogin">记住登录信息</el-checkbox>
								<router-link v-if="containsPhoneMode" class="forgot-pwd" to="/password/reset">忘记密码?</router-link>
							</div>
							<el-button class="submit-btn" type="primary" :loading="loading" @click="submitForm()">登录</el-button>
						</el-form>
					</div>
					<!-- 扫码登录 -->
					<div v-else key="qr" class="login-panel login-panel--qr">
						<QrLogin />
					</div>
					</transition>
				</div>

				<div class="card-footer">
					<div class="footer-row">
						<router-link class="register-link" to="/register">没有账号? 立即注册</router-link>
					</div>
				</div>
			</div>
		</div>

		<template #extra>
			<captcha-image ref="captchaRef"></captcha-image>
		</template>
	</auth-layout>
</template>

<script>
import { saveAuthSession } from '../api/authSession';
import AuthLayout from '../components/common/AuthLayout.vue';
import CaptchaImage from '../components/common/CaptchaImage.vue';
import QrLogin from '../components/login/QrLogin.vue';
import loginCredentialStore from '../api/loginCredentialStore';
import { getStableDeviceId } from '../utils/deviceId';
import { syncAuthWindow } from '../utils/authWindow';
import { trimOuterWhitespace, validateUsername, validatePassword } from '../utils/authValidators';
import pkg from '../../package.json';

const LAST_LOGIN_USER_KEY = 'loginLastUserByTenant';

export default {
	name: 'login',
	components: {
		AuthLayout,
		CaptchaImage,
		QrLogin
	},
	data() {
		return {
			loading: false,
			historyLoading: false,
			isAutoLogin: true,
			isPasswordVisible: false,
			lastMessageMeta: {
				key: '',
				at: 0
			},
			activeCredentialUserName: '',
			showCredentialDropdown: false,
			tenantKey: '',
			credentialHistory: [],
			loginForm: {
				terminal: this.$enums.TERMINAL_TYPE.WEB,
				userName: '',
				password: '',
				deviceId: ''
			},
			rules: {
				userName: [{
					validator: validateUsername,
					trigger: 'blur'
				}],
				password: [{
					validator: validatePassword,
					trigger: 'blur'
				}]
			},
			loginMode: 'password'
		};
	},
	methods: {
		shouldUseBrowserAuthFallback() {
			return !this.configStore.electronMode;
		},
		showDedupedMessage(type, message) {
			const normalizedType = typeof type === 'string' ? type : 'info';
			const normalizedMessage = typeof message === 'string' ? message.trim() : '';
			if (!normalizedMessage) {
				return;
			}
			const now = Date.now();
			const key = `${normalizedType}:${normalizedMessage}`;
			if (this.lastMessageMeta.key === key && now - this.lastMessageMeta.at < 1200) {
				return;
			}
			this.lastMessageMeta = {
				key,
				at: now
			};
			this.$message({
				type: normalizedType,
				message: normalizedMessage
			});
		},
		async initializeLoginState() {
			this.showCredentialDropdown = false;
			this.isAutoLogin = true;
			this.$elm.setTitleTip('', {
				userName: ''
			});
			this.configStore.loadConfig().catch((error) => {
				console.error('加载系统配置失败:', error);
			});
			this.loginForm.terminal = this.resolveTerminal();
			this.refreshTenantKey();
			getStableDeviceId().then((deviceId) => {
				this.loginForm.deviceId = deviceId;
			});
			syncAuthWindow();
			await this.loadCredentialHistory();
			if (this.credentialHistory.length > 0) {
				this.applyCredentialRecord(this.credentialHistory[0]);
				this.rememberUserName(this.credentialHistory[0].userName);
				return;
			}
			const rememberedUserName = this.shouldUseBrowserAuthFallback()
				? (this.getRememberedUserName() || localStorage.getItem('username') || '')
				: '';
			if (rememberedUserName) {
				this.loginForm.userName = rememberedUserName;
			}
		},
		findCredentialByUserName(userName) {
			const normalizedUserName = typeof userName === 'string' ? userName.trim() : '';
			if (!normalizedUserName) {
				return null;
			}
			return this.credentialHistory.find((item) => item.userName === normalizedUserName) || null;
		},
		applyCredentialRecord(record, options = {}) {
			if (!record) {
				return;
			}
			this.loginForm.userName = record.userName || '';
			this.loginForm.password = record.password || '';
			this.activeCredentialUserName = record.userName || '';
			if (!options.focusPassword) {
				return;
			}
			this.$nextTick(() => {
				if (this.$refs.passwordInput && typeof this.$refs.passwordInput.focus === 'function') {
					this.$refs.passwordInput.focus();
				}
			});
		},
		getCredentialApi() {
			if (typeof window !== 'undefined' && window.electronAPI && window.electronAPI.loginCredential) {
				return {
					listByTenant: (payload) => window.electronAPI.loginCredential.list(payload),
					restore: (payload) => window.electronAPI.loginCredential.restore(payload),
					save: (payload) => window.electronAPI.loginCredential.save(payload),
					remove: (payload) => window.electronAPI.loginCredential.remove(payload),
					clearTenant: (payload) => window.electronAPI.loginCredential.clearTenant(payload)
				};
			}
			return loginCredentialStore;
		},
		async loadCredentialHistory(options = {}) {
			if (!this.tenantKey) {
				this.historyLoading = false;
				this.credentialHistory = [];
				return;
			}
			this.historyLoading = true;
			try {
				const result = await this.getCredentialApi().listByTenant({
					tenantKey: this.tenantKey,
					limit: 10
				});
				this.credentialHistory = Array.isArray(result) ? result : [];
				if (!this.credentialHistory.length) {
					this.showCredentialDropdown = false;
				}
				const hasCurrentUser = this.credentialHistory.some((item) => item.userName === this.loginForm.userName);
				if (!hasCurrentUser && !options.preserveCurrentPassword) {
					this.loginForm.password = '';
					this.activeCredentialUserName = '';
				}
			} catch (error) {
				console.error('加载登录历史失败:', error);
				this.credentialHistory = [];
			} finally {
				this.historyLoading = false;
			}
		},
		async restoreRememberedCredential(userName) {
			if (!this.tenantKey || !userName) {
				return null;
			}
			try {
				const record = await this.getCredentialApi().restore({
					tenantKey: this.tenantKey,
					userName
				});
				if (!record) {
					return null;
				}
				this.applyCredentialRecord(record);
				return record;
			} catch (error) {
				console.error('恢复登录历史失败:', error);
				return null;
			}
		},
		onUserNameInput(value) {
			if (this.showCredentialDropdown) {
				this.closeCredentialDropdown();
			}
			const match = this.findCredentialByUserName(value);
			if (match) {
				this.loginForm.password = match.password || '';
				this.activeCredentialUserName = match.userName || '';
				return;
			}
			if (this.activeCredentialUserName && value !== this.activeCredentialUserName) {
				this.loginForm.password = '';
				this.activeCredentialUserName = '';
			}
		},
		toggleCredentialDropdown() {
			if (this.historyLoading || !this.credentialHistory.length) {
				return;
			}
			this.showCredentialDropdown = !this.showCredentialDropdown;
		},
		closeCredentialDropdown() {
			this.showCredentialDropdown = false;
		},
		handleDocumentClick(event) {
			const wrap = this.$refs.credentialInputWrap;
			if (!wrap) {
				return;
			}
			if (wrap.contains(event.target)) {
				return;
			}
			this.closeCredentialDropdown();
		},
		async onSelectCredential(record) {
			if (!record) {
				return;
			}
			this.isAutoLogin = true;
			this.closeCredentialDropdown();
			this.applyCredentialRecord(record, {
				focusPassword: true
			});
		},
		async onRemoveCredential(record) {
			if (!record || !this.tenantKey) {
				return;
			}
			try {
				await this.getCredentialApi().remove({
					tenantKey: this.tenantKey,
					userName: record.userName
				});
				if (this.loginForm.userName === record.userName) {
					this.loginForm.password = '';
				}
				if (this.getRememberedUserName() === record.userName) {
					this.rememberUserName('');
				}
				await this.loadCredentialHistory();
				if (!this.credentialHistory.length) {
					this.closeCredentialDropdown();
				}
				if (this.loginForm.userName === record.userName) {
					if (this.credentialHistory.length > 0) {
						this.applyCredentialRecord(this.credentialHistory[0]);
						this.rememberUserName(this.credentialHistory[0].userName);
					} else {
						this.loginForm.userName = '';
					}
				}
			} catch (error) {
				this.showDedupedMessage('error', '移除历史账号失败，请稍后重试');
			}
		},
		switchPasswordVisible() {
			this.isPasswordVisible = !this.isPasswordVisible;
		},
		async persistCredentialRecord(payload) {
			if (!payload || !this.tenantKey) {
				return;
			}
			if (this.isAutoLogin) {
				await this.getCredentialApi().save({
					tenantKey: this.tenantKey,
					userName: payload.userName,
					password: payload.password,
					source: this.configStore.electronMode ? 'electron' : 'browser'
				});
				this.rememberUserName(payload.userName);
				return;
			}
			await this.getCredentialApi().remove({
				tenantKey: this.tenantKey,
				userName: payload.userName
			});
			this.rememberUserName('');
		},
		readRememberedUserMap() {
			try {
				const cached = localStorage.getItem(LAST_LOGIN_USER_KEY);
				const parsed = JSON.parse(cached || '{}');
				return parsed && typeof parsed === 'object' ? parsed : {};
			} catch (error) {
				return {};
			}
		},
		getRememberedUserName() {
			if (!this.tenantKey) {
				return '';
			}
			const rememberedMap = this.readRememberedUserMap();
			return typeof rememberedMap[this.tenantKey] === 'string' ? rememberedMap[this.tenantKey] : '';
		},
		rememberUserName(userName) {
			if (!this.tenantKey) {
				return;
			}
			const normalizedUserName = typeof userName === 'string' ? userName.trim() : '';
			const rememberedMap = this.readRememberedUserMap();
			if (normalizedUserName) {
				rememberedMap[this.tenantKey] = normalizedUserName;
				localStorage.setItem('username', normalizedUserName);
			} else {
				delete rememberedMap[this.tenantKey];
				localStorage.removeItem('username');
			}
			localStorage.setItem(LAST_LOGIN_USER_KEY, JSON.stringify(rememberedMap));
		},
		normalizeLoginForm() {
			this.loginForm.userName = trimOuterWhitespace(this.loginForm.userName);
			this.loginForm.password = trimOuterWhitespace(this.loginForm.password);
		},
		submitForm() {
			if (this.loading) {
				return;
			}
			this.normalizeLoginForm();
			if (!this.tenantKey) {
				this.showDedupedMessage('warning', '请先选择租户');
				this.onSwitchTenant();
				return;
			}
			this.$refs.loginForm.validate((valid) => {
					if (!valid) {
						return;
					}
					this.loading = true;
					const terminal = this.resolveTerminal();
					const deviceType = this.resolveDeviceType();
					const loginPayload = { ...this.loginForm };
					Promise.all([getStableDeviceId(), this.getAppVersion()]).then(([deviceId, appVersion]) => {
						return this.$http({
							url: '/login',
							method: 'post',
							data: {
								...loginPayload,
								terminal,
								deviceId,
								deviceType,
								appVersion
							}
						});
					}).then(async (data) => {
						if (!data) {
							return;
						}
						localStorage.setItem('isAutoLogin', JSON.stringify(this.isAutoLogin));
						if (this.isAutoLogin) {
							localStorage.setItem('username', this.loginForm.userName);
						} else {
							localStorage.removeItem('username');
						}
						await this.persistCredentialRecord(loginPayload);
						saveAuthSession(data);
						await this.loadCredentialHistory({
							preserveCurrentPassword: true
						});
						this.$elm.setTitleTip('', {
							userName: this.loginForm.userName
						});
						this.closeCredentialDropdown();
						await this.$router.push('/home/chat');
						this.loginForm.password = '';
						this.$message.success('登录成功');
					}).finally(() => {
						this.loading = false;
					});
				});
			},
		resolveTerminal() {
			return this.configStore.electronMode ? this.$enums.TERMINAL_TYPE.PC : this.$enums.TERMINAL_TYPE.WEB;
		},
		resolveDeviceType() {
			return this.configStore.electronMode ? 'pc' : 'web';
		},
		getAppVersion() {
			if (window.electronAPI && window.electronAPI.invoke) {
				return window.electronAPI.invoke('get-app-version').catch(() => process.env.VUE_APP_VERSION || pkg.version || '');
			}
			return Promise.resolve(process.env.VUE_APP_VERSION || pkg.version || '');
		},
		resetForm(formName) {
			this.$refs[formName].resetFields();
		},
		getCookie(name) {
			let reg = new RegExp('(^| )' + name + '=([^;]*)(;|$)');
			let arr = document.cookie.match(reg)
			if (arr) {
				return unescape(arr[2]);
			}
			return '';
		},
		setCookie(name, value) {
			document.cookie = name + '=' + escape(value);
		},
		refreshTenantKey() {
			const endpoint = this.$tenantEndpoint.getCurrentSession();
			const rememberedTenantKey = this.$tenantEndpoint.getRememberedTenantKey();
			this.tenantKey = (endpoint && endpoint.tenantKey) || rememberedTenantKey || '';
		},
		onSwitchTenant() {
			this.$router.push('/tenant?manual=1&redirect=/login');
		},
		formatCredentialTime(value) {
			if (!value) {
				return '';
			}
			const date = new Date(value);
			if (Number.isNaN(date.getTime())) {
				return '';
			}
			return `${date.getMonth() + 1}-${date.getDate()} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
		},
		formatCredentialSource(source) {
			return source === 'electron' ? '桌面端' : 'Web';
		}
	},
	mounted() {
		document.addEventListener('click', this.handleDocumentClick);
		this.initializeLoginState();
	},
	beforeDestroy() {
		document.removeEventListener('click', this.handleDocumentClick);
	},
	computed: {
		tenantDisplayValue() {
			return this.tenantKey || '未选择租户号';
		},
		loginNamePlaceholder() {
			return this.containsPhoneMode ? '用户名或手机号' : '用户名';
		},
		containsPhoneMode() {
			return this.modes.includes('phone')
		},
		modes() {
			return (this.configStore.globalConfig?.registration?.mode || []).filter((item) => item !== 'email')
		}
	},
	watch: {
		'loginForm.userName'(value) {
			const normalized = trimOuterWhitespace(value);
			if (normalized !== value) {
				this.loginForm.userName = normalized;
			}
		},
		'loginForm.password'(value) {
			const normalized = trimOuterWhitespace(value);
			if (normalized !== value) {
				this.loginForm.password = normalized;
			}
		}
	}
}
</script>

<style scoped lang="scss">
@import "../assets/style/auth-card";
@import "../assets/style/auth-preview";

.auth-card {
	overflow: visible;
}

.card-content {
	position: relative;
	overflow: visible;
}

.tenant-context-switcher {
	max-width: 72%;
	min-width: 0;
	display: inline-flex;
	align-items: center;
	gap: 6px;
	height: 32px;
	padding: 0 10px;
	border: 1px solid rgba(var(--im-black-rgb), 0.08);
	border-radius: 999px;
	background: rgba(var(--im-black-rgb), 0.025);
	color: var(--im-text-color);
	cursor: pointer;
	transition: border-color 0.2s ease, background 0.2s ease;

	i {
		flex-shrink: 0;
		font-size: 13px;
		color: var(--im-color-primary);
	}

	span {
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		font-size: 12px;
	}

	strong {
		flex-shrink: 0;
		font-size: 12px;
		font-weight: 500;
		color: var(--im-color-primary);
	}

	&:hover {
		border-color: rgba(var(--im-primary-rgb), 0.24);
		background: rgba(var(--im-primary-rgb), 0.06);
	}
}

.tenant-context-switcher--empty span {
	color: var(--im-text-color-light);
}

.login-panel-shell {
	--login-panel-shell-height: 274px;
	min-height: var(--login-panel-shell-height);
	display: flex;
	align-items: stretch;
}

.login-panel {
	width: 100%;
	height: 100%;
}

.login-panel--qr {
	display: flex;
	align-items: stretch;

	:deep(.qr-login-container) {
		width: 100%;
		height: 100%;
	}
}

.tab-fade-enter-active,
.tab-fade-leave-active {
	transition: opacity 0.18s ease, transform 0.18s ease;
}

.tab-fade-enter,
.tab-fade-leave-to {
	opacity: 0;
	transform: translateY(4px);
}

.nav-tool-bar {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;

	.forgot-pwd {
		text-decoration: none;
		color: var(--im-text-color-light);
		font-size: 12px;
		transition: color 0.2s ease;

		&:hover { color: var(--im-color-primary); }
	}
}

.password-switch-icon {
	cursor: pointer;
	color: var(--im-text-color-light);
	font-size: 16px;
	padding: 0 4px;
}

.credential-input-wrap {
	position: relative;
	width: 100%;
}

.credential-input-wrap :deep(.el-input) {
	display: block;
	width: 100%;
}

.credential-input-wrap :deep(.el-input__inner) {
	width: 100%;
	padding-right: 40px;
}

.credential-input-wrap :deep(.el-input__suffix) {
	right: 8px;
}

.credential-input-wrap :deep(.el-input__suffix-inner) {
	display: inline-flex;
	align-items: center;
	height: 100%;
}

.credential-toggle {
	margin: 0;
	border: 0;
	background: transparent;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	cursor: pointer;
	color: var(--im-text-color-light);
	font-size: 14px;
	padding: 0 4px;
	transition: color 0.2s ease, transform 0.2s ease;
}

.credential-toggle--active {
	color: var(--im-color-primary);
	transform: rotate(180deg);
}

.credential-toggle--loading {
	cursor: default;
}

.credential-toggle--disabled {
	cursor: default;
	color: var(--im-text-color-lighter);
}

.credential-toggle:disabled {
	pointer-events: none;
}

.credential-history-dropdown {
	position: absolute;
	top: calc(100% + 8px);
	left: 0;
	right: 0;
	z-index: 20;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	gap: 6px;
	max-height: 224px;
	padding: 8px;
	overflow-y: auto;
	border: 1px solid rgba(var(--im-black-rgb), 0.08);
	border-radius: 12px;
	background: var(--im-surface-panel-strong);
	box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
}

.credential-history__item {
	width: 100%;
	border: 0;
	border-radius: 8px;
	background: transparent;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 8px 10px;
	cursor: pointer;
	transition: background 0.2s ease, transform 0.2s ease;
}

.credential-history__item:hover {
	background: rgba(var(--im-primary-rgb), 0.08);
	transform: translateY(-1px);
}

.credential-history__main {
	min-width: 0;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
}

.credential-history__name {
	font-size: 13px;
	color: var(--im-text-color);
	font-weight: 500;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.credential-history__meta {
	margin-top: 2px;
	font-size: 11px;
	color: var(--im-text-color-light);
}

.credential-history__remove {
	flex-shrink: 0;
	padding: 4px;
	border-radius: 999px;
	color: var(--im-text-color-light);
	cursor: pointer;
}

.credential-history__remove:hover {
	background: rgba(var(--im-black-rgb), 0.08);
	color: var(--im-text-color);
}

.card-footer {
	.footer-row {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-wrap: wrap;
		gap: 8px;
	}

	.register-link {
		font-size: 13px;
		color: var(--im-text-color-light);
		text-decoration: none;
		transition: color 0.2s ease;

		&:hover { color: var(--im-color-primary); }
	}
}

// 预览插图
@keyframes previewFloat {
	0%, 100% { transform: translateY(0px); }
	50%       { transform: translateY(-6px); }
}
@keyframes bubbleIn {
	from { opacity: 0; transform: scale(0.88) translateY(6px); }
	to   { opacity: 1; transform: scale(1) translateY(0); }
}

.preview-card {
	animation: previewFloat 4s ease-in-out infinite;
	animation-delay: 1s;
}

.preview-msg {
	display: flex;
	align-items: flex-end;
	gap: 8px;
	animation: bubbleIn 0.4s ease both;

	&:nth-child(1) { animation-delay: 1.1s; }
	&:nth-child(2) { animation-delay: 1.35s; }
	&:nth-child(3) { animation-delay: 1.6s; }

	&--right { flex-direction: row-reverse; }
}

.preview-avatar {
	width: 28px;
	height: 28px;
	border-radius: 50%;
	background: var(--im-background-active);
	flex-shrink: 0;

	&--primary { background: var(--im-color-primary-light-6); }
}

.preview-bubble {
	max-width: 180px;
	padding: 7px 11px;
	border-radius: 2px 12px 12px 12px;
	font-size: 12px;
	line-height: 1.45;
	background: var(--im-background-active);
	color: var(--im-text-color);

	&--primary {
		background: var(--im-color-primary);
		color: #fff;
		border-radius: 12px 2px 12px 12px;
	}
}

</style>
